import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { DeepDiveSection } from 'apps/sigma2-ui/src/components/DeepDiveSection';
import { TopOpportunityCard } from 'apps/sigma2-ui/src/components/TopOpportunityCard';
import { UserAnalysesOverlay } from 'apps/sigma2-ui/src/components/UserAnalysesOverlay';
import { useOnUserAnalysisCancelCallback } from 'apps/sigma2-ui/src/lib/hooks/useOnUserAnalysisCancelCallback';
import { BiasAnalysisRequest } from 'libs/end-to-end/src/custom-viz/BiasAnalysisTypes';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { MetricSelect } from 'apps/sigma2-ui/src/lib/hooks/useDynamicMetrics';
import { useDynamicMetrics } from 'apps/sigma2-ui/src/lib/hooks/useDynamicMetrics';
import { useHasChangedSinceSubmission } from 'apps/sigma2-ui/src/lib/hooks/useHasChangedSinceSubmission';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';
import { FilterBar } from 'apps/sigma2-ui/src/components/FilterBar';
import { sortTopThreeOpportunities } from 'apps/sigma2-ui/src/lib/top-opportunities';
import { TopOpportunity } from 'apps/sigma2-ui/src/lib/top-opportunities';
import { determineSecondaryFilterDefinitionsForFeature } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getFiltersFromSecondaryFilterSelections } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSecondaryFilterSelectionsFromCustomVizFormat } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { PrimaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { SecondaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { findDimensionForLevel } from 'libs/end-to-end/src/deep-dive-section-utils';
import { getDeepDivePrimaryFilterSelectionsFromRequest } from 'libs/end-to-end/src/deep-dive-section-utils';
import { getFormattedDeepDiveSectionData } from 'libs/end-to-end/src/deep-dive-section-utils';
import { getDeepDiveSectionSelectedTreatment } from 'libs/end-to-end/src/deep-dive-section-utils';
import { getDeepDiveSubTabTabDefinitions } from 'apps/sigma2-ui/src/components/DeepDiveSection.utils';
import { getDimension } from 'libs/end-to-end/src/dimension-utils';
import { getReferenceLevel } from 'libs/end-to-end/src/deep-dive-section-utils';
import { getTreatmentGroup } from 'libs/end-to-end/src/deep-dive-section-utils';
import { useFilters } from 'apps/sigma2-ui/src/filters-v2/lib/useFilters';
import { parseCustomVizResult } from 'apps/sigma2-ui/src/lib/hooks/useCustomVizAutoload';
import { BiasAnalysisOutput } from 'libs/end-to-end/src/bias-analysis';
import { useCustomViz } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomViz';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { DeepDiveExplanatoryFactorsTab } from 'apps/sigma2-ui/src/components/DeepDiveExplanatoryFactorsTab';
import { DeepDiveOverviewCard } from 'apps/sigma2-ui/src/components/DeepDiveOverviewCard';
import { DeepDiveProcessBiasTab } from 'apps/sigma2-ui/src/components/DeepDiveProcessBiasTab';
import { RecommendedSolutionsSection } from 'apps/sigma2-ui/src/components/RecommendedSolutionsSection';
import { TreatmentSelectionModal } from 'apps/sigma2-ui/src/components/TreatmentSelectionModal';
import { CustomVizCircularSpinner } from 'apps/sigma2-ui/src/components/ui/CustomVizCircularSpinner';
import { SubTabs } from 'apps/sigma2-ui/src/components/ui/SubTabs';
import { HorizontalFilterBar } from 'apps/sigma2-ui/src/filters-v2/components/HorizontalFilterBar';
import { useShareLinkRequest } from 'apps/sigma2-ui/src/lib/hooks/useShareLinkRequest';
import { useShareLinkSearchParameter } from 'apps/sigma2-ui/src/lib/hooks/useShareLinkRequest';
import { useSubTabs } from 'apps/sigma2-ui/src/lib/hooks/useSubTabGroup';
import { useTypedPagination } from 'apps/sigma2-ui/src/lib/hooks/useTypedPagination';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { Nullable } from 'libs/end-to-end/src/ui-types';
import { DeepDiveEmptyDataState } from 'apps/sigma2-ui/src/focus-area/deep-dive/DeepDiveEmptyLoadingAndErrorStates';
import { DeepDiveFailureState } from 'apps/sigma2-ui/src/focus-area/deep-dive/DeepDiveEmptyLoadingAndErrorStates';
import { EntitledDetailsForFocusAreaPage } from 'libs/end-to-end/src/focus-area-utils';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { topTreatments } from 'apps/sigma2-ui/src/models/user';
import { useAnalytics } from 'apps/sigma2-ui/src/lib/hooks/useAnalytics';
import { TopOpportunitiesSection } from 'apps/sigma2-ui/src/components/TopOpportunitiesSection';
import { FilterDefinitionForExtras } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { useScrollToBottom } from 'apps/sigma2-ui/src/lib/hooks/useScrollToBottom';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { GetCustomVizRequestArgs } from 'apps/sigma2-ui/src/filters-v2/components/SaveAnalysisButton';
import { BiasTestIdToVideoLinkRecord } from 'libs/end-to-end/src/bias-test-public';
import { ExplainerVideoModal } from 'apps/sigma2-ui/src/components/ExplainerVideoModal';
import { getBiasAnalysisControlVariableDisplayNames } from 'libs/end-to-end/src/bias-analysis-utils';
import { getPreferredPerformanceMetricForMyObservation } from 'libs/end-to-end/src/my-observation-utils';
import { getMetricByNameFromObservation } from 'libs/end-to-end/src/me';
import { CustomVizHookResult } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { CustomVizHookStatus } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { getTranslatedContentAsString } from 'libs/end-to-end/src/translation-content';
import { DataRefreshIndicator } from 'apps/sigma2-ui/src/components/custom-viz/DataRefreshIndicator';

export const FocusAreaNewDataIntelligenceSummaryInterface = ({
  focusAreaDefinition,
  entitledDetailsForFocusAreaPage,
  observation,
  organizationId,
}: {
  focusAreaDefinition: MyFocusAreaDefinition;
  entitledDetailsForFocusAreaPage: EntitledDetailsForFocusAreaPage;
  observation: MyObservation;
  organizationId: string;
}) => {
  const { userAuthenticatedApiCall, myUser } = useUser();

  const language = useLanguage();
  const { allLevels } = useDimensionConfig();

  const metricsPayload = useDynamicMetrics({
    focusArea: focusAreaDefinition.focusArea,
    type: 'single',
    automaticallySelectFirstPreferred: true,
  });

  const {
    hasChangedSinceSubmission: selectedMetricHasChangedSinceSubmission,
    trackSubmission: trackSelectedMetricSubmission,
    lastSubmittedValue: lastSubmittedMetric,
  } = useHasChangedSinceSubmission({
    initialValue: metricsPayload.preferredMetric?.columnHeaderValue ?? '',
    currentValue: metricsPayload.selectedMetric,
  });

  const context = useCustomViz();
  const { doViz } = context;

  const getCustomVizRequest = useCallback(
    ({
      metric,
      primaryFilterSelections,
      secondaryFilterSelections,
      focusArea,
    }: GetCustomVizRequestArgs & { metric: Nullable<string> }): {
      customVizRequest: BiasAnalysisRequest | undefined;
    } => {
      const treatmentGroup = getTreatmentGroup(primaryFilterSelections);
      const dimension = getDimension(primaryFilterSelections);

      if (!metric || !treatmentGroup || !dimension || !focusArea) {
        return { customVizRequest: undefined };
      }

      const filters = getFiltersFromSecondaryFilterSelections({
        secondaryFilterSelections,
      });

      const customVizRequest: BiasAnalysisRequest = {
        customVizId: 'biasAnalysis',
        organizationId,
        observationLocationType: observation.type,
        inputs: {
          extras: {
            dimension,
            focusArea,
            metric,
            filters,
            treatmentDimensionLevel: treatmentGroup,
          },
          dropdowns: {},
        },
      };

      return {
        customVizRequest,
      };
    },
    [organizationId, observation],
  );

  const onAnalyzeWithParams = useCallback(
    ({
      metric,
      primaryFilterSelections,
      secondaryFilterSelections,
      focusArea,
    }: {
      metric: Nullable<string>;
      primaryFilterSelections: PrimaryFilterSelection[];
      secondaryFilterSelections: SecondaryFilterSelection[];
      focusArea: FocusArea;
    }) => {
      const { customVizRequest } = getCustomVizRequest({
        metric,
        primaryFilterSelections,
        secondaryFilterSelections,
        focusArea,
      });

      if (!customVizRequest) {
        return;
      }

      trackSelectedMetricSubmission(metric);

      doViz(customVizRequest);
    },
    [trackSelectedMetricSubmission, doViz, getCustomVizRequest],
  );

  const metricDefinition = useMemo(() => {
    return (
      focusAreaDefinition.metrics.find((m) => {
        return m.columnHeaderValue === lastSubmittedMetric;
      }) ?? null
    );
  }, [focusAreaDefinition, lastSubmittedMetric]);

  const shareLinkRawData = useShareLinkSearchParameter();

  const filterPayload = useFilters({
    feature: 'dataIntelligenceDeepDive',
    focusArea: focusAreaDefinition.focusArea,
    disabled: false,
    metricDefinitionWhiteLabeling: metricDefinition?.whiteLabeling ?? null,
    analysisName: undefined,
    onApply: useCallback(
      ({
        primaryFilterSelections,
        secondaryFilterSelections,
        isProgrammaticChange,
        isInitialApply,
      }: {
        primaryFilterSelections: PrimaryFilterSelection[];
        secondaryFilterSelections: SecondaryFilterSelection[];
        isProgrammaticChange: boolean;
        isInitialApply: boolean;
      }) => {
        // This prevents onShareLinkLoaded from triggering two custom viz
        // requests. Without this, calling setFilters triggers a new custom viz
        // But in the case of a share link, we need to manually call the custom
        // viz so we can pass the metric, which these filters don't know about
        if (isProgrammaticChange) {
          return;
        }

        // This prevents from loading the initial filters if a share link is applied to the page.
        if (isInitialApply && shareLinkRawData !== null) {
          return;
        }

        onAnalyzeWithParams({
          focusArea: focusAreaDefinition.focusArea,
          primaryFilterSelections,
          secondaryFilterSelections,
          metric: lastSubmittedMetric,
        });
      },
      [
        shareLinkRawData,
        onAnalyzeWithParams,
        focusAreaDefinition.focusArea,
        lastSubmittedMetric,
      ],
    ),
    getCustomVizRequest: useCallback(
      ({
        primaryFilterSelections,
        secondaryFilterSelections,
        focusArea,
      }: GetCustomVizRequestArgs): {
        customVizRequest: BiasAnalysisRequest | undefined;
      } => {
        const { customVizRequest } = getCustomVizRequest({
          primaryFilterSelections,
          secondaryFilterSelections,
          focusArea,
          metric: lastSubmittedMetric,
        });

        return {
          customVizRequest,
        };
      },
      [getCustomVizRequest, lastSubmittedMetric],
    ),
  });

  const dimensionsConfigPayload = useDimensionConfig();

  const dimension = useMemo(
    () => getDimension(filterPayload.primaryFilterSelections),
    [filterPayload.primaryFilterSelections],
  );

  const treatmentGroup = useMemo(
    () => getTreatmentGroup(filterPayload.primaryFilterSelections),
    [filterPayload.primaryFilterSelections],
  );

  const referenceGroup = useMemo(
    () => getReferenceLevel(filterPayload.primaryFilterSelections),
    [filterPayload.primaryFilterSelections],
  );

  const { isLoading, readyToDisplayDynamicContent } = useMemo(() => {
    return parseCustomVizResult({
      result: context.result,
      status: context.status,
      ResultZodType: BiasAnalysisOutput,
    });
  }, [context.result, context.status]);

  const deepDiveRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useScrollToBottom({
    bottomOfTableDivRef: deepDiveRef,
  });

  const onShareLinkLoaded = useCallback(
    (request: CustomVizRequest | null) => {
      if (!request || request.customVizId !== 'biasAnalysis') {
        return;
      }

      const filters = request.inputs.extras.filters;

      const secondaryFilterSelections =
        getSecondaryFilterSelectionsFromCustomVizFormat({
          feature: 'dataIntelligenceDeepDive',
          focusArea: focusAreaDefinition.focusArea,
          customVizFilters: filters,
          focusAreas: observation.focusAreas,
          filterSetDefinitionsWithValues:
            observation.filterSetDefinitionsWithValues,
          dimensionDefinitions: dimensionsConfigPayload.allDimensions,
          analysisName: undefined,
          pipelineAnalysisConfiguration:
            observation.pipelineAnalysisConfiguration,
        });

      const primaryFilterSelections =
        getDeepDivePrimaryFilterSelectionsFromRequest({
          request,
          getPrivilegedLevelForDimension:
            dimensionsConfigPayload.getPrivilegedLevelForDimension,
          getLevelByColumnValue: dimensionsConfigPayload.getLevelByColumnValue,
        });

      filterPayload.setFilters({
        primaryFilterSelections,
        secondaryFilterSelections,
      });

      const metric = request.inputs.extras.metric;
      metricsPayload.onSelectSingle(metric);

      onAnalyzeWithParams({
        metric,
        primaryFilterSelections,
        secondaryFilterSelections,
        focusArea: request.inputs.extras.focusArea,
      });

      scrollToBottom.prepareToScrollToBottom();
      scrollToBottom.scrollToBottomIfNecessary();
    },
    [
      focusAreaDefinition.focusArea,
      observation.focusAreas,
      observation.filterSetDefinitionsWithValues,
      observation.pipelineAnalysisConfiguration,
      dimensionsConfigPayload.allDimensions,
      dimensionsConfigPayload.getPrivilegedLevelForDimension,
      dimensionsConfigPayload.getLevelByColumnValue,
      filterPayload,
      metricsPayload,
      onAnalyzeWithParams,
      scrollToBottom,
    ],
  );

  useShareLinkRequest({
    onLoaded: onShareLinkLoaded,
    myUser,
    canLoad: filterPayload.isInitialized,
    userAuthenticatedApiCall,
    type: BiasAnalysisRequest,
  });

  const onUserAnalysisCancel = useOnUserAnalysisCancelCallback({
    onShareLinkLoaded,
    resetFiltersToInitialState: filterPayload.resetFiltersToInitialState,
    resetMetricsToInitialState: metricsPayload.resetMetricsToInitialState,
    resetDimensionsToInitialState: null,
  });

  const topThreeOpportunities: TopOpportunity[] = useMemo(() => {
    const availableInsights = observation.insightsV2.filter(
      (insight) =>
        insight.focusArea === focusAreaDefinition.focusArea &&
        insight.metric === lastSubmittedMetric,
    );

    return sortTopThreeOpportunities({
      insights: availableInsights,
      focusAreaDefinitions: [focusAreaDefinition],
      allLevels,
      showMetricsWithoutBiasTestTasks: true,
    });
  }, [allLevels, focusAreaDefinition, lastSubmittedMetric, observation]);

  const topRecommendedSolutionsTreatments = useMemo(() => {
    return topTreatments({
      myObservation: observation,
      focusArea: focusAreaDefinition.focusArea,
      metric: metricDefinition?.columnHeaderValue ?? undefined,
      dimensionKey: dimension ?? undefined,
      levelKeys: [referenceGroup, treatmentGroup].filter(Boolean) as string[],
    }).slice(0, 3);
  }, [
    observation,
    focusAreaDefinition,
    metricDefinition,
    referenceGroup,
    treatmentGroup,
    dimension,
  ]);

  const { updateTreatments } = useObservation();

  const [selectedTopTreatmentIndex, setSelectedTopTreatmentIndex] =
    useState<Nullable<number>>(null);

  const analytics = useAnalytics();

  const onTreatmentClick = useCallback(
    (newSelectedTopTreatmentIndex: number) => {
      analytics.trackEvent({
        eventName: 'Recommended Solution Card Clicked',
        eventProperties: {
          currentPage: 'Focus Area Page',
          focusArea: focusAreaDefinition.focusArea,
        },
      });
      setSelectedTopTreatmentIndex(newSelectedTopTreatmentIndex);
    },
    [analytics, focusAreaDefinition.focusArea],
  );

  const onTopOpportunityCardClick = useCallback(
    (opportunity: TopOpportunity) => {
      const treatmentDimensionLevel =
        opportunity.treatmentLevelDefinition.columnValue;

      const dimension = findDimensionForLevel({
        allDimensions: dimensionsConfigPayload.allDimensions,
        level: treatmentDimensionLevel,
      })?.columnHeaderValue;

      if (!dimension) {
        return;
      }

      // Anytime top opportunities get clicked, they are in the context of all
      // filters applied. So by doing this, we receive all of the filter options
      // and apply them to the custom viz request
      const allPossibleSecondaryFilterSelections: FilterDefinitionForExtras[] =
        determineSecondaryFilterDefinitionsForFeature({
          feature: 'dataIntelligenceDeepDive',
          focusArea: focusAreaDefinition.focusArea,
          focusAreas: observation.focusAreas,
          filterSetDefinitionsWithValues:
            observation.filterSetDefinitionsWithValues,
          dimensionDefinitions: dimensionsConfigPayload.allDimensions,
          analysisName: undefined,
          pipelineAnalysisConfiguration:
            observation.pipelineAnalysisConfiguration,
        }).map((definition) => {
          return {
            columnHeaderValue: definition.columnHeaderValue,
            values: definition.uniqueValues,
          };
        });

      const request: BiasAnalysisRequest = {
        customVizId: 'biasAnalysis',
        organizationId,
        observationLocationType: observation.type,
        inputs: {
          dropdowns: {},
          extras: {
            metric: opportunity.metricDefinition.columnHeaderValue,
            dimension,
            focusArea: focusAreaDefinition.focusArea,
            filters: allPossibleSecondaryFilterSelections,
            treatmentDimensionLevel,
          },
        },
      };

      onShareLinkLoaded(request);
    },
    [
      dimensionsConfigPayload.allDimensions,
      focusAreaDefinition.focusArea,
      observation.filterSetDefinitionsWithValues,
      observation.focusAreas,
      observation.pipelineAnalysisConfiguration,
      observation.type,
      onShareLinkLoaded,
      organizationId,
    ],
  );

  useEffect(() => {
    analytics.trackEvent({
      eventName: 'Focus Area Page Viewed',
      eventProperties: {
        selectedFocusArea: focusAreaDefinition.focusArea,
        selectedTab: 'SummaryV2',
      },
    });
  }, [analytics, focusAreaDefinition.focusArea]);

  const { customVizRequest: lastCustomVizRequest } = useMemo(
    () =>
      getCustomVizRequest({
        metric: lastSubmittedMetric,
        focusArea: focusAreaDefinition.focusArea,
        primaryFilterSelections: filterPayload.primaryFilterSelections,
        secondaryFilterSelections: filterPayload.secondaryFilterSelections,
      }),
    [
      filterPayload.primaryFilterSelections,
      filterPayload.secondaryFilterSelections,
      focusAreaDefinition.focusArea,
      getCustomVizRequest,
      lastSubmittedMetric,
    ],
  );

  return (
    <>
      <Spacing size={'3xl'} direction={'vertical'}>
        <UserAnalysesOverlay onUserAnalysisCancel={onUserAnalysisCancel} />

        {metricsPayload.metrics &&
          metricsPayload.metrics.length > 1 &&
          entitledDetailsForFocusAreaPage.atLeastOneSectionShowing && (
            <FilterBar
              dynamicFiltersPayload={'none'}
              onAnalyze={() => {
                onAnalyzeWithParams({
                  focusArea: focusAreaDefinition.focusArea,
                  primaryFilterSelections:
                    filterPayload.primaryFilterSelections,
                  secondaryFilterSelections:
                    filterPayload.secondaryFilterSelections,
                  metric: metricsPayload.selectedMetric,
                });
              }}
              staticFilters={
                <MetricSelect
                  {...metricsPayload}
                  isProcessing={metricsPayload.isProcessing || isLoading}
                />
              }
              areStaticFiltersEqual={() =>
                !selectedMetricHasChangedSinceSubmission
              }
            />
          )}

        {entitledDetailsForFocusAreaPage.entitledToBiasAnalysis && (
          <TopOpportunitiesSection
            opportunities={topThreeOpportunities}
            metricDefinition={metricDefinition}
            calibrationFraming={observation.calibrationFraming}
            card={({ opportunity, key }) => {
              const onCardClick = () => onTopOpportunityCardClick(opportunity);

              return (
                <TopOpportunityCard
                  key={key}
                  metricDefinition={opportunity.metricDefinition}
                  focusAreaDefinition={opportunity.focusAreaDefinition}
                  referenceLevelDefinition={
                    opportunity.referenceLevelDefinition
                  }
                  treatmentLevelDefinition={
                    opportunity.treatmentLevelDefinition
                  }
                  rawDisparityPercentage={opportunity.rawDisparityPercentage}
                  controlledDisparityPercentage={
                    opportunity.controlledDisparityPercentage
                  }
                  debiasedDisparityPercentage={
                    opportunity.debiasedDisparityPercentage
                  }
                  onCardClick={onCardClick}
                  cardIsClickable={!isLoading}
                  calibrationFraming={observation.calibrationFraming}
                />
              );
            }}
          />
        )}

        {entitledDetailsForFocusAreaPage.entitledToBiasAnalysis && (
          <Spacing
            ref={deepDiveRef}
            size={'md'}
            direction={'vertical'}
            className="@container"
          >
            <DeepDiveSection
              readyToDisplayDynamicContent={readyToDisplayDynamicContent}
              filterBar={
                <HorizontalFilterBar
                  items={filterPayload.items}
                  slotRight={
                    <DataRefreshIndicator
                      status={context.status}
                      resultCreatedAt={context.resultCreatedAt}
                    />
                  }
                />
              }
              recommendedSolutions={
                entitledDetailsForFocusAreaPage.areSolutionsEnabled ? (
                  <RecommendedSolutionsSection
                    onTreatmentClick={onTreatmentClick}
                    topTreatments={topRecommendedSolutionsTreatments}
                  />
                ) : null
              }
              overviewCard={
                <div className="flex min-h-96">
                  <FocusAreaNewDataIntelligenceSummaryOverviewCard
                    customVizRequest={lastCustomVizRequest}
                    result={context.result}
                    status={context.status}
                    resetFiltersToInitialState={
                      filterPayload.resetFiltersToInitialState
                    }
                  />
                </div>
              }
              treatmentSelectionModal={
                <TreatmentSelectionModal
                  maxWidthOverride="max-w-screen-md"
                  show={selectedTopTreatmentIndex !== null}
                  title={
                    <S2Typography
                      variant={'headline-4-alt'}
                      translatedContent={language.focusAreaKeyTakeawayCardSolutionTitle()}
                      textColorVariant="white"
                    />
                  }
                  topTreatments={{
                    treatments: getDeepDiveSectionSelectedTreatment({
                      topTreatments: topRecommendedSolutionsTreatments,
                      selectedTopTreatmentIndex,
                    }),
                  }}
                  updateTreatments={updateTreatments}
                  onClose={() => {
                    setSelectedTopTreatmentIndex(null);
                  }}
                />
              }
            />
          </Spacing>
        )}
      </Spacing>
    </>
  );
};

export const FocusAreaNewDataIntelligenceSummaryOverviewCard = ({
  customVizRequest,
  result,
  status,
  resetFiltersToInitialState,
}: {
  customVizRequest: BiasAnalysisRequest | undefined;
  result: CustomVizHookResult;
  status: CustomVizHookStatus;
  resetFiltersToInitialState?: () => void;
}) => {
  const { myUser } = useUser();

  const language = useLanguage();
  const { myObservation } = useObservation();

  const focusArea = customVizRequest?.inputs.extras.focusArea;
  const lastSubmittedMetric = customVizRequest?.inputs.extras.metric;
  const dimension = customVizRequest?.inputs.extras.dimension;
  const treatmentGroup =
    customVizRequest?.inputs.extras.treatmentDimensionLevel;

  const focusAreaDefinition = useMemo(() => {
    return myObservation?.focusAreas.find(
      (fA) => fA.focusArea === customVizRequest?.inputs.extras.focusArea,
    );
  }, [customVizRequest, myObservation]);

  const metricDefinition = useMemo(() => {
    return (
      focusAreaDefinition?.metrics.find((m) => {
        return m.columnHeaderValue === lastSubmittedMetric;
      }) ?? null
    );
  }, [focusAreaDefinition, lastSubmittedMetric]);

  const dimensionsConfigPayload = useDimensionConfig();

  const dimensionDefinition = useMemo(() => {
    if (!dimension) {
      return null;
    }

    return (
      dimensionsConfigPayload.getDimensionByColumnHeaderValue(dimension) ?? null
    );
  }, [dimensionsConfigPayload, dimension]);

  const treatmentGroupLevelDefinition = useMemo(() => {
    if (!dimension || !treatmentGroup) {
      return null;
    }

    return (
      dimensionsConfigPayload.getLevelByColumnValue(
        dimension,
        treatmentGroup,
      ) ?? null
    );
  }, [dimensionsConfigPayload, treatmentGroup, dimension]);

  const referenceGroupLevelDefinition = useMemo(() => {
    if (!dimension) {
      return null;
    }

    return dimensionsConfigPayload.getPrivilegedLevelForDimension(dimension);
  }, [dimensionsConfigPayload, dimension]);

  const performanceMetricDefinition: Nullable<MyMetricDefinition> =
    useMemo(() => {
      return getPreferredPerformanceMetricForMyObservation(myObservation);
    }, [myObservation]);

  const { data, isLoading, readyToDisplayDynamicContent, hasFailed } =
    useMemo(() => {
      return parseCustomVizResult({
        result: result,
        status: status,
        ResultZodType: BiasAnalysisOutput,
      });
    }, [result, status]);

  const cleanData = useMemo(() => {
    return (
      focusArea &&
      getFormattedDeepDiveSectionData({
        data,
        treatmentGroup: treatmentGroup ?? null,
        referenceGroup: referenceGroupLevelDefinition?.columnValue ?? null,
        metric: lastSubmittedMetric ?? null,
        dimensionDefinition,
        focusArea,
        allDimensions: dimensionsConfigPayload.allDimensions,
      })
    );
  }, [
    data,
    treatmentGroup,
    referenceGroupLevelDefinition?.columnValue,
    lastSubmittedMetric,
    dimensionDefinition,
    focusArea,
    dimensionsConfigPayload.allDimensions,
  ]);

  const analytics = useAnalytics();

  const imageRefProcessBiasesRef = useRef<HTMLInputElement | null>(null);
  const imageRefExplanatoryFactorsRef = useRef<HTMLInputElement | null>(null);

  const onProcessBiasPageChange = useCallback(
    (pageIndex: number) => {
      analytics.trackEvent({
        eventName: 'Deep Dive Used Carousel',
        eventProperties: {
          selectedTab: 'Process Bias',
          pageIndex,
        },
      });
    },
    [analytics],
  );

  const onExplanatoryFactorsPageChange = useCallback(
    (pageIndex: number) => {
      analytics.trackEvent({
        eventName: 'Deep Dive Used Carousel',
        eventProperties: {
          selectedTab: 'Explanatory Factors',
          pageIndex,
        },
      });
    },
    [analytics],
  );

  const processBiasesPagination = useTypedPagination({
    items: cleanData?.visualizations ?? [],
    onPageChange: onProcessBiasPageChange,
  });

  const explanatoryFactorsPagination = useTypedPagination({
    items: cleanData?.explanatoryFactors ?? [],
    onPageChange: onExplanatoryFactorsPageChange,
  });

  const [viewBiasTestVideoUrl, setViewBiasTestVideoUrl] = useState<
    string | null
  >(null);

  const [showBiasesVideo, setShowBiasesVideo] = useState(false);

  const onViewBiasTestVideoClick = useCallback((id: number) => {
    const videoLink = BiasTestIdToVideoLinkRecord[id];
    if (videoLink) {
      setViewBiasTestVideoUrl(videoLink);
      setShowBiasesVideo(true);
    }
  }, []);

  // eslint-disable-next-line complexity
  const tabDefinitions = useMemo(() => {
    return focusAreaDefinition && myObservation
      ? getDeepDiveSubTabTabDefinitions({
          language,
          readyToDisplayDynamicContent,
          processBiasesCount: cleanData?.visualizations.length,
          processBiasesNode: (
            <DeepDiveProcessBiasTab
              focusAreaDefinition={focusAreaDefinition}
              metricDefinition={metricDefinition}
              dimensionDefinition={dimensionDefinition}
              treatmentGroupLevelDefinition={treatmentGroupLevelDefinition}
              referenceGroupLevelDefinition={
                referenceGroupLevelDefinition ?? null
              }
              groupResult={processBiasesPagination.selectedItem}
              carouselSwitcher={processBiasesPagination.carouselSwitcher}
              graphRef={imageRefProcessBiasesRef}
              performanceMetricDefinition={performanceMetricDefinition}
              organizationName={myUser?.organization?.name ?? null}
              onViewBiasTestVideoClick={onViewBiasTestVideoClick}
              calibrationFraming={myObservation?.calibrationFraming}
              controlledDisparity={cleanData?.controlledDisparity ?? null}
            />
          ),
          processBiasesHidden: !getHasBiasTestTasksForMetricByObservation({
            metricName: metricDefinition?.columnHeaderValue ?? '',
            focusArea: focusAreaDefinition.focusArea,
            myObservation,
          }),
          explanatoryFactorsCount: cleanData?.explanatoryFactors.length,
          explanatoryFactorsNode: (
            <DeepDiveExplanatoryFactorsTab
              calibrationFraming={myObservation.calibrationFraming}
              focusAreaDefinition={focusAreaDefinition}
              metricDefinition={metricDefinition}
              dimensionDefinition={dimensionDefinition}
              treatmentGroupLevelDefinition={treatmentGroupLevelDefinition}
              referenceGroupLevelDefinition={
                referenceGroupLevelDefinition ?? null
              }
              groupResult={explanatoryFactorsPagination.selectedItem}
              carouselSwitcher={explanatoryFactorsPagination.carouselSwitcher}
              graphRef={imageRefExplanatoryFactorsRef}
              performanceMetricDefinition={performanceMetricDefinition}
              controlledDisparity={cleanData?.controlledDisparity ?? null}
            />
          ),
          calibrationFraming: myObservation.calibrationFraming,
        })
      : [];
  }, [
    focusAreaDefinition,
    myObservation,
    language,
    readyToDisplayDynamicContent,
    cleanData,
    metricDefinition,
    dimensionDefinition,
    treatmentGroupLevelDefinition,
    referenceGroupLevelDefinition,
    processBiasesPagination.selectedItem,
    processBiasesPagination.carouselSwitcher,
    performanceMetricDefinition,
    myUser?.organization?.name,
    onViewBiasTestVideoClick,
    explanatoryFactorsPagination.selectedItem,
    explanatoryFactorsPagination.carouselSwitcher,
  ]);

  const subTabProps = useSubTabs({
    tabDefinitions,
  });

  const controlVariableDisplayNames = useMemo(() => {
    return focusAreaDefinition
      ? getBiasAnalysisControlVariableDisplayNames({
          focusAreaDefinition,
          metricDefinition,
        })
      : [];
  }, [focusAreaDefinition, metricDefinition]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        h="full"
        flex="1"
        w="full"
      >
        <CustomVizCircularSpinner />
      </Box>
    );
  }

  if (!hasFailed && !isLoading && !cleanData) {
    return (
      <DeepDiveEmptyDataState
        onResetFiltersClick={resetFiltersToInitialState}
      />
    );
  }

  if (hasFailed) {
    return <DeepDiveFailureState />;
  }

  return (
    treatmentGroupLevelDefinition &&
    referenceGroupLevelDefinition &&
    metricDefinition &&
    cleanData &&
    myObservation && (
      <>
        {' '}
        {viewBiasTestVideoUrl && (
          <ExplainerVideoModal
            videoUrl={viewBiasTestVideoUrl}
            open={showBiasesVideo}
            title={getTranslatedContentAsString(
              language.learnAboutBiasesModalTitle(),
            )}
            onClose={() => {
              setShowBiasesVideo(false);
              setViewBiasTestVideoUrl(null);
            }}
          />
        )}
        <Spacing
          className="flex flex-col @5xl:flex-row w-full"
          direction="horizontal"
          size="md"
        >
          <div className="flex flex-auto w-full @5xl:w-2/5">
            <DeepDiveOverviewCard
              treatmentGroupLevelDefinition={treatmentGroupLevelDefinition}
              referenceGroupLevelDefinition={referenceGroupLevelDefinition}
              numeratorOfTreatmentGroupSubjects={
                cleanData.treatmentGroupNumerator
              }
              numeratorOfReferenceGroupSubjects={
                cleanData.referenceGroupNumerator
              }
              metricDefinition={metricDefinition}
              rawDisparityPercentage={cleanData.rawDisparity}
              controlledDisparityPercentage={cleanData.controlledDisparity}
              debiasedDisparityPercentage={cleanData.debiasDisparity}
              confidencePercentage={cleanData.certainty}
              numberOfTreatmentGroupSubjects={cleanData.treatmentGroupCount}
              numberOfReferenceGroupSubjects={cleanData.referenceGroupCount}
              controlVariableDisplayNames={controlVariableDisplayNames}
              calibrationFraming={myObservation.calibrationFraming}
            />
          </div>
          <div className="flex flex-auto w-full @5xl:w-3/5">
            <Box h="full" w="full">
              <SubTabs {...subTabProps} />
            </Box>
          </div>
        </Spacing>
      </>
    )
  );
};

export const getHasBiasTestTasksForMetricByObservation = ({
  myObservation,
  metricName,
  focusArea,
}: {
  myObservation: MyObservation;
  metricName: string;
  focusArea: FocusArea;
}) => {
  const metricDefinition = getMetricByNameFromObservation({
    metricName,
    focusArea,
    myObservation,
  });
  return metricDefinition?.hasBiasTestTasks ?? false;
};
